/**
 * AutomatchWizard — bulk-review screen for Flow B (auto-match imported tasks
 * against the project's published standard leaves). On open it runs a single
 * write-free preview (scores every non-summary imported task against the standard
 * leaves → matched / unmatched, deriving a norm quantity per matched row). The
 * planner bulk-confirms per row (accept toggle, inline qty override, per-row
 * baseline switch, BOQ paste), then Create mappings commits with the frozen
 * automatchId (server re-derives to gate). Already-mapped tasks are read-only and
 * skipped; unmatched tasks are listed non-blocking.
 * Visual register: Sovereign Emerald (Antd 6 — styles.{header,body,footer}).
 */
import {
    Alert,
    Button,
    Checkbox,
    Collapse,
    Input,
    InputNumber,
    Modal,
    Spin,
    Switch,
    Table,
    Tag,
    message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { isAxiosError } from 'axios';
import { ClipboardPaste, Sparkles, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { SOVEREIGN } from '../../utils/epcProjectVisual';
import { parseBomPaste } from '../apply/applyBom';
import { useCommitAutomatch, usePreviewAutomatch } from '../hooks/useAutomatch';
import type { AutomatchMatchedRow, AutomatchPreview } from '../types';

export interface AutomatchWizardProps {
    open: boolean;
    projectId: string;
    /** default: latest ready snapshot (server resolves when omitted). */
    importId?: string;
    onClose: () => void;
    onDone: () => void;
}

function apiErrMsg(err: unknown, fallback: string): string {
    const msg = (err as { response?: { data?: { message?: string } } } | undefined)?.response
        ?.data?.message;
    return msg || fallback;
}

function errCode(err: unknown): string | undefined {
    return (err as { response?: { data?: { code?: string } } } | undefined)?.response?.data?.code;
}

const CAPTION = 'mb-2 text-[10px] font-bold uppercase tracking-[0.12em]';

/** ≥90 green · 70-89 gold. */
function scoreColor(score: number): string {
    return score >= 90 ? SOVEREIGN.brand : SOVEREIGN.champagne;
}

export function AutomatchWizard({ open, projectId, importId, onClose, onDone }: AutomatchWizardProps) {
    const previewMut = usePreviewAutomatch(projectId);
    const commitMut = useCommitAutomatch(projectId);

    const [preview, setPreview] = useState<AutomatchPreview | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    // Per-row local state, keyed by taskKey.
    const [acceptMap, setAcceptMap] = useState<Map<string, boolean>>(new Map());
    const [qtyMap, setQtyMap] = useState<Map<string, number>>(new Map());
    const [baselineMap, setBaselineMap] = useState<Map<string, boolean>>(new Map());
    const [bomOpen, setBomOpen] = useState(false);
    const [bomText, setBomText] = useState('');
    const [bomErrors, setBomErrors] = useState<string[]>([]);

    /** Seed local per-row state from a fresh preview (default accept TRUE for matched
     *  rows unless already mapped; baseline default ON). */
    const seedRowState = (p: AutomatchPreview) => {
        const accept = new Map<string, boolean>();
        const baseline = new Map<string, boolean>();
        for (const m of p.matched) {
            accept.set(m.taskKey, !m.existingMapping);
            baseline.set(m.taskKey, true);
        }
        setAcceptMap(accept);
        setBaselineMap(baseline);
        setQtyMap(new Map());
    };

    // ── One preview per open ────────────────────────────────────────────────
    // biome-ignore lint/correctness/useExhaustiveDependencies: mutation objects are unstable per render; run once per open
    useEffect(() => {
        if (!open) {
            // reset on close
            setPreview(null);
            setPreviewError(null);
            setAcceptMap(new Map());
            setQtyMap(new Map());
            setBaselineMap(new Map());
            setBomOpen(false);
            setBomText('');
            setBomErrors([]);
            return;
        }
        void runPreview();
    }, [open]);

    const runPreview = async () => {
        setPreviewError(null);
        try {
            const p = await previewMut.mutateAsync({ ...(importId ? { importId } : {}) });
            setPreview(p);
            seedRowState(p);
        } catch (e) {
            setPreview(null);
            setPreviewError(
                apiErrMsg(
                    e,
                    'No standard or ready schedule to match — import or apply a schedule first.',
                ),
            );
        }
    };

    // ── BOQ paste ───────────────────────────────────────────────────────────
    const matchedKeySet = useMemo(
        () => new Set((preview?.matched ?? []).map((m) => m.taskKey)),
        [preview],
    );

    const handleBomOk = () => {
        const { overrides, errors } = parseBomPaste(bomText, matchedKeySet);
        if (errors.length > 0) {
            setBomErrors(errors);
            return; // nothing partial
        }
        setQtyMap((prev) => {
            const next = new Map(prev);
            for (const o of overrides) next.set(o.taskKey, o.qty);
            return next;
        });
        message.success(
            `${overrides.length} ${overrides.length === 1 ? 'quantity' : 'quantities'} applied from BOQ`,
        );
        setBomOpen(false);
        setBomText('');
        setBomErrors([]);
    };

    // ── Derived counts ──────────────────────────────────────────────────────
    const matched = preview?.matched ?? [];
    const acceptedCount = useMemo(
        () =>
            matched.reduce(
                (n, m) => n + (!m.existingMapping && (acceptMap.get(m.taskKey) ?? false) ? 1 : 0),
                0,
            ),
        [matched, acceptMap],
    );
    const blocked = !!preview && preview.blocks.length > 0;
    const committing = commitMut.isPending;
    const canCreate = !!preview && !blocked && acceptedCount > 0 && !committing;

    // ── Commit ──────────────────────────────────────────────────────────────
    const handleCreate = async () => {
        if (!preview || blocked || acceptedCount < 1) return;
        const rows = preview.matched.map((m) => ({
            taskKey: m.taskKey,
            accept: !m.existingMapping && (acceptMap.get(m.taskKey) ?? false),
            ...(qtyMap.has(m.taskKey) ? { qtyOverride: qtyMap.get(m.taskKey) as number } : {}),
            generateBaseline: baselineMap.get(m.taskKey) ?? true,
        }));
        try {
            const res = await commitMut.mutateAsync({
                automatchId: preview.automatchId,
                ...(importId ? { importId } : {}),
                rows,
            });
            const failed = res.generationsFailed.length;
            message.success(
                `Created ${res.mappingsCreated} mappings${failed ? `, ${failed} generation(s) failed` : ''}`,
            );
            onDone();
            onClose();
        } catch (e) {
            const code = errCode(e);
            if (isAxiosError(e) && e.response?.status === 409) {
                if (code === 'APPLY_IN_PROGRESS') {
                    message.error('Another apply is already in progress on this project.');
                    return;
                }
                // AUTOMATCH_STALE (or any other 409): the derivation drifted — re-preview.
                message.warning('Inputs changed — preview refreshed');
                await runPreview();
                return;
            }
            message.error(apiErrMsg(e, 'Create mappings failed'));
        }
    };

    // ── Matched table columns ───────────────────────────────────────────────
    const columns: ColumnsType<AutomatchMatchedRow> = [
        {
            title: 'Accept',
            key: 'accept',
            width: 72,
            render: (_v, row) => (
                <Checkbox
                    checked={!row.existingMapping && (acceptMap.get(row.taskKey) ?? false)}
                    disabled={row.existingMapping}
                    aria-label={`Accept ${row.name}`}
                    onChange={(e) =>
                        setAcceptMap((prev) => {
                            const next = new Map(prev);
                            next.set(row.taskKey, e.target.checked);
                            return next;
                        })
                    }
                />
            ),
        },
        {
            title: 'Imported task',
            key: 'task',
            render: (_v, row) => (
                <div>
                    <div
                        className="text-[12.5px] font-semibold"
                        style={{ color: row.existingMapping ? SOVEREIGN.kpiCaption : SOVEREIGN.ink }}
                    >
                        {row.name}
                    </div>
                    <div
                        className="font-mono text-[11px]"
                        style={{ color: SOVEREIGN.kpiCaption }}
                    >
                        {row.taskKey}
                        {row.existingMapping && (
                            <span className="ml-2 font-sans italic">already mapped</span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: 'Match',
            key: 'match',
            render: (_v, row) => (
                <div className="flex flex-col items-start gap-1">
                    <span className="text-[12.5px] font-semibold" style={{ color: SOVEREIGN.title }}>
                        {row.leafName}
                    </span>
                    <span className="text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                        {row.templateName} v{row.templateVersion}
                    </span>
                    <Tag
                        style={{
                            margin: 0,
                            borderColor: scoreColor(row.score),
                            color: scoreColor(row.score),
                            background: 'transparent',
                        }}
                    >
                        {Math.round(row.score)}%
                    </Tag>
                </div>
            ),
        },
        {
            title: 'Qty',
            key: 'qty',
            width: 200,
            render: (_v, row) => {
                const overridden = qtyMap.has(row.taskKey);
                const value = overridden ? (qtyMap.get(row.taskKey) as number) : row.qty.value;
                const provenance = overridden ? 'overridden' : row.qty.provenance;
                return (
                    <div className="flex items-center gap-2">
                        <InputNumber
                            size="small"
                            min={0}
                            value={value}
                            disabled={row.existingMapping}
                            aria-label={`Quantity for ${row.name}`}
                            onChange={(v) =>
                                setQtyMap((prev) => {
                                    const next = new Map(prev);
                                    if (typeof v === 'number' && v > 0) next.set(row.taskKey, v);
                                    else next.delete(row.taskKey);
                                    return next;
                                })
                            }
                            style={{
                                width: 90,
                                background: SOVEREIGN.inputBg,
                                borderColor: SOVEREIGN.inputBorder,
                            }}
                        />
                        <span className="text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                            {row.qty.unit}
                        </span>
                        <Tag
                            style={{
                                margin: 0,
                                borderColor: SOVEREIGN.cardBorder,
                                color: SOVEREIGN.bottle,
                                background: 'transparent',
                            }}
                        >
                            {provenance}
                        </Tag>
                    </div>
                );
            },
        },
        {
            title: 'Baseline',
            key: 'baseline',
            width: 88,
            render: (_v, row) => (
                <Switch
                    size="small"
                    checked={baselineMap.get(row.taskKey) ?? true}
                    disabled={row.existingMapping}
                    aria-label={`Generate baseline for ${row.name}`}
                    onChange={(checked) =>
                        setBaselineMap((prev) => {
                            const next = new Map(prev);
                            next.set(row.taskKey, checked);
                            return next;
                        })
                    }
                />
            ),
        },
    ];

    const previewing = previewMut.isPending;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={1100}
            destroyOnHidden
            maskClosable={false}
            title={
                <span className="inline-flex items-center gap-2">
                    <Sparkles size={16} aria-hidden style={{ color: SOVEREIGN.brand }} />
                    <span className="text-[15px] font-bold" style={{ color: SOVEREIGN.title }}>
                        Auto-match templates
                    </span>
                    {preview && (
                        <span
                            className="text-[11.5px] font-semibold"
                            style={{ color: SOVEREIGN.kpiCaption }}
                        >
                            {preview.standardName} · v{preview.standardVersion}
                        </span>
                    )}
                </span>
            }
            styles={{
                header: {
                    background: SOVEREIGN.canvas,
                    borderBottom: `1px solid ${SOVEREIGN.cardBorder}`,
                    paddingBottom: 12,
                },
                body: {
                    background: SOVEREIGN.paper,
                    padding: 20,
                    maxHeight: '72vh',
                    overflowY: 'auto',
                },
                footer: {
                    background: SOVEREIGN.canvas,
                    borderTop: `1px solid ${SOVEREIGN.cardBorder}`,
                    paddingTop: 12,
                },
            }}
            footer={
                <div className="flex items-center justify-between gap-3">
                    <span
                        className="text-[11.5px] font-semibold tabular-nums"
                        style={{ color: SOVEREIGN.kpiCaption }}
                    >
                        {preview
                            ? `${acceptedCount} of ${preview.matched.length} matched · ${preview.unmatched.length} unmatched`
                            : ''}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={committing}
                            disabled={!canCreate}
                            onClick={() => void handleCreate()}
                            style={
                                canCreate
                                    ? { background: SOVEREIGN.btn, borderColor: SOVEREIGN.btnBorder }
                                    : undefined
                            }
                        >
                            Create mappings
                        </Button>
                    </div>
                </div>
            }
        >
            {previewing && !preview ? (
                <div className="flex justify-center py-16">
                    <Spin />
                </div>
            ) : previewError ? (
                <Alert
                    type="error"
                    showIcon
                    message="Could not run auto-match"
                    description={previewError}
                />
            ) : preview ? (
                <div className="space-y-5">
                    {/* ── Blocks ──────────────────────────────────────────── */}
                    {blocked && (
                        <Alert
                            type="error"
                            showIcon
                            icon={<TriangleAlert size={16} aria-hidden />}
                            message={`Auto-match is blocked — ${preview.blocks.length} ${preview.blocks.length === 1 ? 'issue' : 'issues'} to resolve`}
                            description={
                                <ul className="m-0 max-h-40 list-disc overflow-y-auto pl-5">
                                    {preview.blocks.map((b) => (
                                        <li key={b} className="text-[12px]">
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            }
                        />
                    )}

                    {/* ── Warnings ────────────────────────────────────────── */}
                    {preview.warnings.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            message={`${preview.warnings.length} ${preview.warnings.length === 1 ? 'warning' : 'warnings'}`}
                            description={
                                <ul className="m-0 max-h-40 list-disc overflow-y-auto pl-5">
                                    {preview.warnings.map((w) => (
                                        <li key={w} className="text-[12px]">
                                            {w}
                                        </li>
                                    ))}
                                </ul>
                            }
                        />
                    )}

                    {/* ── Matched table ───────────────────────────────────── */}
                    <section>
                        <div className="mb-2 flex items-center justify-between">
                            <div className={CAPTION} style={{ color: SOVEREIGN.kpiCaption }}>
                                Matched tasks
                            </div>
                            <Button
                                size="small"
                                icon={<ClipboardPaste size={14} aria-hidden />}
                                onClick={() => setBomOpen(true)}
                                disabled={preview.matched.length === 0}
                                style={{ borderColor: SOVEREIGN.btnBorder, color: SOVEREIGN.bottle }}
                            >
                                Paste from BOQ
                            </Button>
                        </div>
                        <Table<AutomatchMatchedRow>
                            size="small"
                            rowKey="taskKey"
                            columns={columns}
                            dataSource={preview.matched}
                            pagination={false}
                            locale={{ emptyText: 'No imported tasks matched a standard leaf.' }}
                        />
                    </section>

                    {/* ── Unmatched panel ─────────────────────────────────── */}
                    {preview.unmatched.length > 0 && (
                        <Collapse
                            size="small"
                            items={[
                                {
                                    key: 'unmatched',
                                    label: (
                                        <span
                                            className="text-[12px] font-semibold"
                                            style={{ color: SOVEREIGN.champagne }}
                                        >
                                            {preview.unmatched.length} unmatched task
                                            {preview.unmatched.length === 1 ? '' : 's'}
                                        </span>
                                    ),
                                    children: (
                                        <div>
                                            <p
                                                className="mb-2 text-[11.5px]"
                                                style={{ color: SOVEREIGN.kpiCaption }}
                                            >
                                                Left unmatched — use the per-task template dialog.
                                            </p>
                                            <ul className="m-0 max-h-48 list-none overflow-y-auto p-0">
                                                {preview.unmatched.map((u) => (
                                                    <li
                                                        key={u.taskKey}
                                                        className="flex items-center justify-between border-b py-1 text-[12px]"
                                                        style={{
                                                            borderColor: SOVEREIGN.innerRule,
                                                            color: SOVEREIGN.ink,
                                                        }}
                                                    >
                                                        <span>
                                                            {u.name}{' '}
                                                            <span
                                                                className="font-mono text-[11px]"
                                                                style={{ color: SOVEREIGN.kpiCaption }}
                                                            >
                                                                {u.taskKey}
                                                            </span>
                                                        </span>
                                                        <span
                                                            className="tabular-nums text-[11px]"
                                                            style={{ color: SOVEREIGN.kpiCaption }}
                                                        >
                                                            best {Math.round(u.bestScore)}%
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    )}
                </div>
            ) : null}

            {/* ── BOQ paste modal ─────────────────────────────────────────── */}
            <Modal
                open={bomOpen}
                onCancel={() => {
                    setBomOpen(false);
                    setBomText('');
                    setBomErrors([]);
                }}
                onOk={handleBomOk}
                okText="Apply quantities"
                width={560}
                destroyOnHidden
                maskClosable={false}
                title={
                    <span className="text-[14px] font-bold" style={{ color: SOVEREIGN.title }}>
                        Paste quantities from BOQ
                    </span>
                }
                styles={{
                    header: {
                        background: SOVEREIGN.canvas,
                        borderBottom: `1px solid ${SOVEREIGN.cardBorder}`,
                        paddingBottom: 12,
                    },
                    body: { background: SOVEREIGN.paper, padding: 20 },
                    footer: {
                        background: SOVEREIGN.canvas,
                        borderTop: `1px solid ${SOVEREIGN.cardBorder}`,
                        paddingTop: 12,
                    },
                }}
                okButtonProps={{
                    style: { background: SOVEREIGN.btn, borderColor: SOVEREIGN.btnBorder },
                }}
            >
                <p
                    className="mb-2 text-[12px] font-semibold"
                    style={{ color: SOVEREIGN.kpiCaption }}
                >
                    One task per line: the task key, then the quantity, separated by a tab, space or
                    comma.
                </p>
                <Input.TextArea
                    value={bomText}
                    onChange={(e) => {
                        setBomText(e.target.value);
                        if (bomErrors.length > 0) setBomErrors([]);
                    }}
                    rows={8}
                    autoFocus
                    placeholder={'1.2.3\t120\n2.4.1  340'}
                    style={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 12,
                        background: SOVEREIGN.inputBg,
                        borderColor: SOVEREIGN.inputBorder,
                    }}
                />
                {bomErrors.length > 0 && (
                    <Alert
                        className="mt-3"
                        type="error"
                        showIcon
                        message={`${bomErrors.length} ${bomErrors.length === 1 ? 'line' : 'lines'} could not be parsed — nothing was applied`}
                        description={
                            <ul className="m-0 max-h-40 list-disc overflow-y-auto pl-5">
                                {bomErrors.map((e) => (
                                    <li key={e} className="text-[12px]">
                                        {e}
                                    </li>
                                ))}
                            </ul>
                        }
                    />
                )}
            </Modal>
        </Modal>
    );
}

export default AutomatchWizard;
